import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, CheckCircle2, ListTodo, BarChart3, Bot, Map, Sparkles } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import CTASection from "@/components/site/CTASection";
import { Seo } from "@/components/seo/Seo";

const modules = [
  { id: "calendar", label: "Content Calendar", icon: CalendarDays },
  { id: "approvals", label: "Approval System", icon: CheckCircle2 },
  { id: "tasks", label: "Tasks", icon: ListTodo },
  { id: "reports", label: "Reports", icon: BarChart3 },
  { id: "ai", label: "AI Assistant", icon: Bot },
  { id: "roadmaps", label: "Roadmaps", icon: Map },
];

const Chip = ({ children, tone = "muted" }) => {
  const map = { blue: "bg-[#38BDF8]/15 text-[#0b6f96]", green: "bg-emerald-500/15 text-emerald-700", amber: "bg-amber-500/15 text-amber-700", muted: "bg-[#2E1B16]/8 text-[#2E1B16]/60" };
  return <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${map[tone]}`}>{children}</span>;
};

const Calendar = () => (
  <div className="grid grid-cols-7 gap-2">
    {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="text-[11px] font-bold text-[#2E1B16]/40 text-center pb-1">{d}</div>)}
    {Array.from({ length: 28 }).map((_, i) => {
      const items = { 3: ["Reel"], 8: ["Shoot", "Story"], 12: ["Ad set"], 15: ["Blog"], 19: ["Reel", "Email"], 24: ["Case study"] }[i];
      return (
        <div key={i} className="min-h-[64px] rounded-lg border border-[#2E1B16]/8 p-1.5 bg-white">
      <Seo title="Client Portal" description="Client portal for Viral Vision Media clients." noindex />
          <div className="text-[10px] text-[#2E1B16]/40 mb-1">{i + 1}</div>
          {items?.map((t) => <div key={t} className="text-[10px] font-semibold rounded bg-[#38BDF8]/15 text-[#0b6f96] px-1 py-0.5 mb-1 truncate">{t}</div>)}
        </div>
      );
    })}
  </div>
);

const Approvals = () => (
  <div className="space-y-3">
    {[["Spring launch, Reel 01", "Pending", "amber"], ["Founder story, long form", "Approved", "green"], ["Product hero photo set", "Changes requested", "blue"], ["Recruitment ad, cut 2", "Approved", "green"]].map(([t, s, tone], i) => (
      <div key={i} className="flex items-center justify-between rounded-xl border border-[#2E1B16]/8 bg-white p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2E1B16]/8" />
          <span className="font-semibold text-sm">{t}</span>
        </div>
        <div className="flex items-center gap-3">
          <Chip tone={tone}>{s}</Chip>
          <button className="text-xs font-bold text-[#38BDF8]">Review</button>
        </div>
      </div>
    ))}
  </div>
);

const Tasks = () => (
  <div className="space-y-2.5">
    {[["Approve April calendar", true], ["Send product samples for shoot", true], ["Review ad creative, round 2", false], ["Sign off new landing page", false], ["Book Q3 strategy call", false]].map(([t, done], i) => (
      <div key={i} className="flex items-center gap-3 rounded-xl border border-[#2E1B16]/8 bg-white p-3.5">
        <span className={`w-5 h-5 rounded-md flex items-center justify-center ${done ? "bg-[#38BDF8]" : "border border-[#2E1B16]/25"}`}>{done && <CheckCircle2 size={13} className="text-white" />}</span>
        <span className={`text-sm font-medium ${done ? "line-through text-[#2E1B16]/40" : ""}`}>{t}</span>
      </div>
    ))}
  </div>
);

const Reports = () => (
  <div>
    <div className="grid grid-cols-3 gap-3 mb-5">
      {[["Reach", "1.2M", "+218%"], ["Leads", "342", "+64%"], ["ROAS", "4.8x", "+31%"]].map(([k, v, d]) => (
        <div key={k} className="rounded-xl border border-[#2E1B16]/8 bg-white p-4">
          <div className="text-[11px] text-[#2E1B16]/50 font-semibold">{k}</div>
          <div className="font-display text-2xl mt-1">{v}</div>
          <div className="text-[11px] font-bold text-emerald-600">{d}</div>
        </div>
      ))}
    </div>
    <div className="rounded-xl border border-[#2E1B16]/8 bg-white p-5">
      <div className="flex items-end gap-2 h-28">
        {[40, 52, 46, 63, 58, 72, 68, 88].map((h, i) => (
          <div key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%`, background: i === 7 ? "#38BDF8" : "rgba(46,27,22,0.14)" }} />
        ))}
      </div>
    </div>
  </div>
);

const AI = () => (
  <div className="space-y-4">
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-[#38BDF8] flex items-center justify-center shrink-0"><Sparkles size={15} className="text-[#2E1B16]" /></div>
      <div className="rounded-2xl rounded-tl-sm bg-[#2E1B16] text-[#FAF7F2] p-4 text-sm max-w-md">Here's your weekly summary: reach is up 42%, three assets are awaiting approval, and the April calendar is 80% planned. Want me to draft next week's captions?</div>
    </div>
    <div className="flex gap-3 justify-end">
      <div className="rounded-2xl rounded-tr-sm bg-[#F2EFE9] p-4 text-sm max-w-md border border-[#2E1B16]/8">Yes, and summarise last week's strategy call.</div>
    </div>
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-[#38BDF8] flex items-center justify-center shrink-0"><Sparkles size={15} className="text-[#2E1B16]" /></div>
      <div className="rounded-2xl rounded-tl-sm bg-[#2E1B16] text-[#FAF7F2] p-4 text-sm max-w-md">On it. Drafting 5 captions and pulling the call summary now…</div>
    </div>
  </div>
);

const Roadmaps = () => (
  <div className="space-y-3">
    {[["Brand refresh", "Complete", "green", "100%"], ["Content engine setup", "In progress", "blue", "70%"], ["Paid launch", "In progress", "blue", "40%"], ["CRM & automations", "Upcoming", "muted", "10%"]].map(([t, s, tone, w], i) => (
      <div key={i} className="rounded-xl border border-[#2E1B16]/8 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-sm">{t}</span>
          <Chip tone={tone}>{s}</Chip>
        </div>
        <div className="h-2 rounded-full bg-[#2E1B16]/8"><div className="h-2 rounded-full bg-[#38BDF8]" style={{ width: w }} /></div>
      </div>
    ))}
  </div>
);

const views = { calendar: Calendar, approvals: Approvals, tasks: Tasks, reports: Reports, ai: AI, roadmaps: Roadmaps };

const PortalPage = () => {
  const [active, setActive] = useState("calendar");
  const View = views[active];
  const label = modules.find((m) => m.id === active).label;

  return (
    <div data-testid="portal-page">
      <PageHeader
        eyebrow="Client Portal · Public Preview"
        title="One place where everything"
        accent="happens."
        sub="A live look at the workspace every client gets. Click through the modules below. The numbers here are just for the demo."
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="rounded-3xl border border-[#2E1B16]/12 bg-white/60 panel-shadow overflow-hidden">
          <div className="flex items-center gap-1.5 px-5 py-3 border-b border-[#2E1B16]/10 bg-[#F2EFE9]/60">
            <span className="w-2.5 h-2.5 rounded-full bg-[#38BDF8]" /><span className="w-2.5 h-2.5 rounded-full bg-[#2E1B16]/15" /><span className="w-2.5 h-2.5 rounded-full bg-[#2E1B16]/15" />
            <span className="ml-3 text-xs font-semibold text-[#2E1B16]/50">workspace.viralvisionmediaau.com</span>
            <span className="ml-auto text-[10px] font-bold px-2 py-1 rounded-full bg-[#38BDF8]/15 text-[#0b6f96]">MOCK DATA</span>
          </div>
          <div className="grid md:grid-cols-[240px_1fr]">
            <aside className="border-b md:border-b-0 md:border-r border-[#2E1B16]/10 p-4 bg-[#FAF7F2]/50">
              <div className="flex md:flex-col gap-1.5 overflow-x-auto">
                {modules.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setActive(m.id)}
                    data-testid={`portal-tab-${m.id}`}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${active === m.id ? "bg-[#2E1B16] text-[#FAF7F2]" : "text-[#2E1B16]/70 hover:bg-[#2E1B16]/5"}`}
                  >
                    <m.icon size={17} className={active === m.id ? "text-[#38BDF8]" : ""} />
                    {m.label}
                  </button>
                ))}
              </div>
            </aside>
            <div className="p-5 md:p-8 min-h-[440px] bg-[#FAF7F2]/30">
              <h2 className="font-display text-2xl tracking-tight mb-6" data-testid="portal-active-title">{label}</h2>
              <AnimatePresence mode="wait">
                <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
                  <View />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
};

export default PortalPage;
